/**
 * The browse SCOPE the filters control is filtering — from the matched ROUTE's
 * own loader data, and from the URL's query string through the filters codec.
 *
 * THE DEFECT THIS CLOSES (obs. 2). The filters trigger existed in exactly one
 * place, `ListingsTopBar` (`md:hidden`), because the modal's `open` state and
 * its scope props lived in `ListingsClient` — a FEATURE component. The desktop
 * navbar is `infra`, and `infra → feature` is refused by the element DAG
 * (`web/packages/config/element-dag/public.mjs`), so the only two ways to put
 * the same trigger beside the omnibox were to widen the DAG (refused by
 * policy) or to hand the scope down through chrome. Both are drift by
 * construction: two placements holding two copies of "what is being browsed"
 * show two different badges for one URL the moment either copy is missed —
 * which is the same class as the badge that counted zero before the filter set
 * became a required prop.
 *
 * So the scope is not PASSED to any placement. All of them DERIVE it here.
 *
 * ## Where it comes from, and why that moved (§2.9)
 *
 * It used to be derived from the PATHNAME, by this module, in three hand
 * predicates: `pathname.endsWith('/buy')` for the mode, a three-segment split
 * against the raw strings `'districts'` / `'rent'` / `'buy'` for the district
 * facet, and a disjunction of route builders for "is this a browse canvas at
 * all". That is a SECOND COPY of the route table. Renaming a segment in
 * `web/manifests/routes.yaml` moves every builder, every link and every
 * loader, and leaves those three answering about a URL shape that no longer
 * exists — with nothing red, because `filter_param_codec_proto_parity` reaches
 * the query vocabulary and not the path.
 *
 * The route already knows all three facts, and now says so:
 *
 *   * "is this a listings browse canvas" IS `chrome: canvas` on the matched
 *     route — the two hubs, their dwelling-type facets and the two district
 *     facets, which is exactly the set that renders `ListingsClient`;
 *   * the MODE is the route's own (`/listings/buy` is the buy hub because the
 *     manifest says so, not because the string ends in `buy`);
 *   * the district facet's SLUG is the matched route's PARAM, which its loader
 *     has already narrowed to a `DistrictSlug` in order to answer 404.
 *
 * So the canvas loaders state it as `browseScope`, and `webroutesgen` emits
 * one `LoaderMustStateBrowseScope<'<route id>', …>` row per `chrome: canvas`
 * route into the generated server route table — a canvas whose loader omits it
 * is `TS2344` in `//web/apps/public:typecheck` naming the route. A canvas
 * added to the manifest owes the declaration by existing; one that stops being
 * a canvas stops owing it. This file parses no paths at all.
 *
 * ★ What it deliberately does NOT do is fetch. The histogram and the live
 * results count are the modal's own query, scoped by what this returns — a
 * trigger that is merely visible must cost nothing. And the FILTERS half still
 * comes from the live query string rather than from loader data: the modal
 * writes params through `router.setQuery`, so a loader copy of the filter set
 * would be one navigation stale exactly when the badge is read.
 */
import { useSearchParams } from '@/shared/i18n/routing';
import type { ListingsFilterSet } from '@/shared/lib/listings/listings-filter-set';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import {
  decodeListingsFilterParams,
  isListingsMode,
  transactionModeFor,
  type ListingsBrowseScopeData,
  type ListingsMode,
} from '@letsgethome/routes';
import { isDistrictSlug } from '@letsgethome/types/districts';
import { useMatches } from 'react-router';

/** What the browse surface is looking at, as the route and the URL state it. */
export interface ListingsBrowseScope {
  /**
   * The browse mode in the URL vocabulary (`rent` / `buy`) — what the route
   * builders and the search omnibox take.
   */
  readonly urlMode: ListingsMode;
  /**
   * The same mode in the domain vocabulary (`rent` / `sale`) — what the
   * filters modal's price scale and its BFF query take. Converted through the
   * shared `transactionModeFor`, never by a local ternary.
   */
  readonly mode: TransactionModeValue;
  /**
   * The full filter set — the badge's input, in the same string-level shape
   * `ListingsClient` receives from its loader.
   *
   * `districtSlug` is folded in from the matched route's `browseScope`,
   * exactly as `facet.loader.ts` folds it into the set it returns: it is a
   * discrete location that is not a query param, and leaving it out would make
   * the badge on a facet page count one filter fewer than the loader did.
   */
  readonly filters: ListingsFilterSet;
}

/**
 * The browse scope the DEEPEST matched route declares, or `null` when no
 * matched route declares one — i.e. when this URL is not a browse canvas.
 *
 * Deepest wins, exactly as `pageChromeFromMatches` resolves the chrome that
 * put the obligation there: a leaf override is what a deeper declaration is
 * for. The narrowing is ours because React Router types a match's `data` as
 * `unknown`, and a DECLARED-but-malformed `browseScope` THROWS rather than
 * reading as "not a canvas" — the two are different facts, and defaulting one
 * to the other is how a browse page silently loses its controls.
 *
 * Taken as a plain argument rather than read from the hook so both arms are
 * assertable: a throw from a route ELEMENT is swallowed by React Router's own
 * error boundary, which would make a test observe an error page instead of the
 * exception, and the message is the whole value of the throw. (The same
 * argument, and the same shape, as `PageShell`'s three resolvers.)
 */
export function browseScopeFromMatches(
  // Spelled inline, exactly like `PageShell`'s resolvers: this is react-router's
  // match shape, not an entity, and its `id` is a ROUTE id.
  matches: readonly { readonly id: string; readonly data: unknown }[]
): ListingsBrowseScopeData | null {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const data: unknown = match?.data;
    if (typeof data !== 'object' || data === null || !('browseScope' in data)) continue;
    const { browseScope } = data as { browseScope: unknown };
    const scope: { urlMode?: unknown; districtSlug?: unknown } =
      typeof browseScope === 'object' && browseScope !== null ? browseScope : {};
    const rawMode = scope.urlMode;
    const rawSlug = scope.districtSlug;
    const districtSlug = typeof rawSlug === 'string' && isDistrictSlug(rawSlug) ? rawSlug : null;
    if (!isListingsMode(rawMode) || (rawSlug !== undefined && districtSlug === null)) {
      throw new Error(
        `useListingsBrowseScope: route "${match?.id ?? '?'}" declares browseScope=${JSON.stringify(
          browseScope
        )}, which is not a browse scope. webroutesgen REQUIRES every \`chrome: canvas\` route's loader to return \`browseScope: ListingsBrowseScopeData\`.`
      );
    }
    const urlMode: ListingsMode = rawMode;
    return districtSlug === null ? { urlMode } : { urlMode, districtSlug };
  }
  return null;
}

/**
 * The browse scope for the CURRENT route, or `null` when this URL is not a
 * listings browse canvas.
 *
 * ONE derivation answers both "does the navbar show the browse controls" and
 * "what are they scoped to", so the two cannot drift into a navbar rendering a
 * control for a scope the control does not recognise — the property the
 * deleted `isListingsBrowsePath` predicate existed to provide and provided by
 * restating the route table.
 */
export function useListingsBrowseScope(): ListingsBrowseScope | null {
  const matches = useMatches();
  const searchParams = useSearchParams();

  const declared = browseScopeFromMatches(matches);
  if (declared === null) return null;

  const { urlMode, districtSlug } = declared;
  return {
    urlMode,
    mode: transactionModeFor(urlMode),
    filters: {
      ...decodeListingsFilterParams(searchParams),
      ...(districtSlug ? { districtSlug } : {}),
    },
  };
}

/**
 * The browse scope for a component that is MOUNTED ONLY ON A CANVAS — the
 * navbar's browse controls, the canvas's own top bar, the filters control and
 * its modal.
 *
 * It throws rather than returning a nullable, for `pageBandBackTarget`'s
 * reason one layer up: for these four, `null` is not a value, it is a
 * contradiction — the mount happened, so the route said `chrome: canvas`, so
 * the generator put an obligation on its loader. Reaching this throw means the
 * generated table and this tree disagree, and a control that silently rendered
 * the RENT scale on a `buy` canvas is strictly worse than one that says so.
 */
export function useListingsCanvasScope(): ListingsBrowseScope {
  const scope = useListingsBrowseScope();
  if (scope === null) {
    throw new Error(
      'useListingsCanvasScope: no matched route declares `browseScope`, but this component is ' +
        'mounted only on a `chrome: canvas` route. webroutesgen emits a ' +
        '`LoaderMustStateBrowseScope<…>` row for every canvas, so a canvas loader that returns ' +
        'one cannot be missing here — the route table and this tree disagree.'
    );
  }
  return scope;
}
